import { NgTemplateOutlet } from '@angular/common';
import type { AfterViewInit, OnDestroy } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { FreelancerAsyncPipeModule } from '@freelancer/pipes';
import { TimeUtils } from '@freelancer/time-utils';
import { TrackingModule } from '@freelancer/tracking';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { CameraComponent } from '@freelancer/ui/camera';
import { PictureDisplay, PictureObjectFit } from '@freelancer/ui/picture';
import type { Observable } from 'rxjs';
import { Subscription } from 'rxjs';
import { last, map, take } from 'rxjs/operators';
import { ButtonComponent } from '../button/button.component';
import { CameraImageType } from '../camera-input';
import { IconComponent } from '../icon/icon.component';
import { ModalRef } from '../modal/modal-ref';
import { ModalTitleDirective } from '../modal/modal-title.directive';
import { PictureComponent } from '../picture/picture.component';

@Component({
  standalone: true,
  template: `
    <ng-container *flModalTitle>
      {{ modalTitle }}
    </ng-container>
    <div flTrackingSection="AppWebcam">
      <h4 class="text-body-20 mb-small">
        @if (!this.modalHeader) {
          <ng-container i18n="Use your camera modal title">
            Use your camera to take photo
          </ng-container>
        }
        @if (this.modalHeader) {
          {{ modalHeader }}
        }
      </h4>
      <div>
        <ng-container [ngTemplateOutlet]="content"></ng-container>
      </div>
      @if (showCamera) {
        <div class="CameraView mb-small">
          <fl-camera
            [videoDevices]="videoDevices"
            [videoConstraints]="videoConstraints"
            [imageType]="imageType"
          ></fl-camera>
          @if (showCameraTimer) {
            <p
              class="CameraView-timer text-title-90 font-light"
              i18n="Timer countdown when taking photo"
            >
              {{ cameraTimerValue$ | flAsync }}
            </p>
          }
          @if (showCameraSwitcher) {
            <fl-button
              class="CameraView-switcher"
              flTrackingLabel="CameraSwitch"
              [color]="ButtonColor.DEFAULT"
              [disabled]="showCameraTimer"
              [size]="ButtonSize.SMALL"
              (click)="cycleDevice()"
            >
              <fl-icon
                [name]="'ui-camera-switch-v2'"
                [useIconFont]="true"
                [fillIconFont]="true"
              ></fl-icon>
            </fl-button>
          }
        </div>
      }
      @if (!showCamera && imagePreviewSrc) {
        <fl-picture
          class="CameraView-picture mb-small"
          [alignCenter]="true"
          [display]="PictureDisplay.BLOCK"
          [fullWidth]="true"
          [objectFit]="PictureObjectFit.CONTAIN"
          [src]="imagePreviewSrc"
        ></fl-picture>
      }
      <div class="CameraView-footer">
        @if (showCamera) {
          <fl-button
            class="CameraView-action"
            i18n="Take photo button"
            flTrackingLabel="TakeAPhoto"
            [color]="ButtonColor.SECONDARY"
            [disabled]="showCameraTimer || isSwitchingCamera"
            [size]="ButtonSize.LARGE"
            (click)="takePhoto()"
          >
            Take Photo
          </fl-button>
        }
        @if (!showCamera) {
          <fl-button
            class="CameraView-action mr-small"
            i18n="Retake button"
            flTrackingLabel="Retake"
            [color]="ButtonColor.DEFAULT"
            [size]="ButtonSize.LARGE"
            (click)="retakePhoto()"
          >
            Retake
          </fl-button>
        }
        @if (!showCamera) {
          <fl-button
            class="CameraView-action mb-small tablet:mb-none"
            flTrackingLabel="Upload"
            [color]="ButtonColor.SECONDARY"
            [size]="ButtonSize.LARGE"
            (click)="confirm()"
          >
            @if (!this.uploadCTA) {
              <ng-container i18n="Upload button"> Upload </ng-container>
            }
            @if (this.uploadCTA) {
              {{ uploadCTA }}
            }
          </fl-button>
        }
      </div>
    </div>
  `,
  styleUrls: ['./camera-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ModalTitleDirective,
    TrackingModule,
    NgTemplateOutlet,
    ButtonComponent,
    IconComponent,
    PictureComponent,
    FreelancerAsyncPipeModule,
    CameraComponent,
  ],
})
export class CameraModalComponent implements AfterViewInit, OnDestroy {
  private cd = inject(ChangeDetectorRef);
  private modalRef = inject<ModalRef<CameraModalComponent>>(ModalRef);
  private timeUtils = inject(TimeUtils);

  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  PictureDisplay = PictureDisplay;
  PictureObjectFit = PictureObjectFit;

  cameraTimerValue$: Observable<number>;
  imagePreviewSrc: string | undefined = undefined;
  remainingCaptureTime = 0;
  showCamera = true;
  showCameraSwitcher = false;
  showCameraTimer = false;
  isSwitchingCamera = false;

  @RepetitiveSubscription()
  private cameraTimerSubscription?: Subscription;

  @Input() captureTimeout = 5; // in seconds
  @Input() content: TemplateRef<HTMLElement>;
  @Input() modalHeader?: string;
  @Input() modalTitle?: string;
  @Input() uploadCTA?: string;
  @Input() videoDevices: readonly MediaDeviceInfo[];
  @Input() videoConstraints: MediaTrackConstraints;
  @Input() imageType: CameraImageType = CameraImageType.PNG;

  @ViewChild(CameraComponent) camera: CameraComponent;

  ngAfterViewInit(): void {
    this.cycleDevice();
  }

  ngOnDestroy(): void {
    if (this.cameraTimerSubscription) {
      this.cameraTimerSubscription.unsubscribe();
    }
  }

  cancel(): void {
    this.modalRef.close();
  }

  confirm(): void {
    this.modalRef.close(this.imagePreviewSrc);
  }

  cycleDevice(): void {
    this.isSwitchingCamera = true;

    this.camera.cycleDevice().then(() => {
      this.setShowCameraSwitcher(true);
      this.isSwitchingCamera = false;
      this.cd.markForCheck();
    });
  }

  retakePhoto(): void {
    this.setShowCameraSwitcher(true);
    this.setShowCamera(true);

    // Show camera component first before accessing cycleDevice()
    this.timeUtils.setTimeout(() => {
      this.cycleDevice();
    });
  }

  setShowCamera(value: boolean): void {
    this.showCamera = value;
  }

  setShowCameraSwitcher(value: boolean): void {
    this.showCameraSwitcher = value && this.videoDevices.length > 1;
  }

  setShowCameraTimer(value: boolean): void {
    this.showCameraTimer = value;
  }

  takePhoto(): void {
    if (!this.showCameraTimer) {
      this.setShowCameraTimer(true);
      this.setShowCameraSwitcher(false);
      this.cameraTimerValue$ = this.timeUtils.rxTimer(0, 1000).pipe(
        take(this.captureTimeout + 1),
        map(val => this.captureTimeout - val),
      );

      this.cameraTimerSubscription = this.cameraTimerValue$
        .pipe(last(val => val >= this.captureTimeout))
        .subscribe(val => {
          this.imagePreviewSrc = this.camera.getSnapshot();
          this.setShowCameraTimer(false);
          this.setShowCamera(false);
        });
    }
  }
}
